import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Img,
} from 'remotion';

/**
 * TimelineAnimation - 历史时间线动画效果
 * 
 * 适用场景：
 * - 历史事件展示
 * - 发展历程回顾
 * - 项目里程碑展示
 * - 时间轴叙事
 * 
 * 教育价值评分：9/10
 * - 帮助学生理解历史事件的先后顺序
 * - 时间可视化的直观呈现
 * - 适合多种学科的时间线展示
 */

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  icon?: string;
}

interface TimelineAnimationProps {
  events: TimelineEvent[];
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  showProgress?: boolean;
}

export const TimelineAnimation: React.FC<TimelineAnimationProps> = ({
  events,
  primaryColor = '#4A90E2',
  secondaryColor = '#7B68EE',
  backgroundColor = '#0F1419',
  showProgress = true,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 计算时间线进度
  const totalDuration = fps * 8; // 8秒动画
  const progress = Math.min(frame / totalDuration, 1);

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleScale = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });

  // 时间线中心线动画
  const lineProgress = interpolate(progress, [0.1, 0.8], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 事件点位置计算（垂直时间线）
  const timelineHeight = height * 0.7;
  const startY = height * 0.2;
  const eventSpacing = timelineHeight / (events.length + 1);

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `scale(${titleScale})`,
        }}
      >
        <h1
          style={{
            fontSize: 48,
            fontWeight: 700,
            color: '#FFFFFF',
            margin: 0,
            textShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          发展历程
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#A0AEC0',
            marginTop: 8,
          }}
        >
          Historical Timeline
        </p>
      </div>

      {/* 时间线主干 */}
      <div
        style={{
          position: 'absolute',
          left: width * 0.5 - 3,
          top: startY,
          width: 6,
          height: timelineHeight * lineProgress,
          background: `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          borderRadius: 3,
          boxShadow: `0 0 20px ${primaryColor}40`,
        }}
      />

      {/* 事件节点 */}
      {events.map((event, index) => {
        const eventStartProgress = 0.1 + (index / events.length) * 0.7;
        const eventProgress = interpolate(
          progress,
          [eventStartProgress, eventStartProgress + 0.1],
          [0, 1],
          {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }
        );

        const eventY = startY + eventSpacing * (index + 1);
        const isLeft = index % 2 === 0;
        const eventOpacity = spring({
          frame: frame - eventStartProgress * totalDuration,
          fps,
          config: {
            damping: 12,
            stiffness: 80,
          },
        });

        return (
          <React.Fragment key={index}>
            {/* 连接线 */}
            <div
              style={{
                position: 'absolute',
                left: width * 0.5,
                top: eventY,
                width: eventProgress * (width * 0.35),
                height: 2,
                background: `linear-gradient(90deg, ${primaryColor}50 0%, transparent 100%)`,
                transform: `translateX(${isLeft ? -eventProgress * width * 0.35 : 0}px) translateY(-1px)`,
              }}
            />

            {/* 节点圆点 */}
            <div
              style={{
                position: 'absolute',
                left: width * 0.5 - 12,
                top: eventY - 12,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                border: '3px solid #FFFFFF',
                boxShadow: `0 0 20px ${primaryColor}80`,
                opacity: eventProgress,
                transform: `scale(${eventProgress})`,
              }}
            />

            {/* 事件卡片 */}
            <div
              style={{
                position: 'absolute',
                left: isLeft ? width * 0.1 : width * 0.55,
                top: eventY - 50,
                width: width * 0.35,
                opacity: eventOpacity,
                transform: `translateX(${isLeft ? -20 : 20}px) translateY(-50%) scale(${0.9 + eventOpacity * 0.1})`,
              }}
            >
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${primaryColor}30`,
                  borderRadius: 12,
                  padding: '16px 20px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                }}
              >
                {/* 年份 */}
                <div
                  style={{
                    fontSize: 34,
                    fontWeight: 800,
                    color: primaryColor,
                    marginBottom: 8,
                  }}
                >
                  {event.year}
                </div>

                {/* 标题 */}
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    color: '#FFFFFF',
                    marginBottom: 6,
                  }}
                >
                  {event.title}
                </div>

                {/* 描述 */}
                <div
                  style={{
                    fontSize: 22,
                    color: '#A0AEC0',
                    lineHeight: 1.5,
                  }}
                >
                  {event.description}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* 进度指示器 */}
      {showProgress && (
        <div
          style={{
            position: 'absolute',
            bottom: 40,
            left: width * 0.2,
            width: width * 0.6,
            height: 4,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              borderRadius: 2,
            }}
          />
        </div>
      )}
    </AbsoluteFill>
  );
};

// 示例配置
export const exampleTimelineEvents: TimelineEvent[] = [
  {
    year: '1990',
    title: '项目启动',
    description: '首个版本发布，奠定技术基础',
  },
  {
    year: '1995',
    title: '技术突破',
    description: '引入创新算法，性能提升 200%',
  },
  {
    year: '2000',
    title: '全球化',
    description: '进入国际市场，用户突破百万',
  },
  {
    year: '2010',
    title: '移动时代',
    description: '推出移动应用，引领行业变革',
  },
  {
    year: '2020',
    title: 'AI 融合',
    description: '集成人工智能，开启智能新纪元',
  },
];

// Composition 配置
export const timelineAnimationConfig = {
  id: 'TimelineAnimation',
  width: 1920,
  height: 1080,
  durationInFrames: 240,
  fps: 30,
  defaultProps: {
    events: exampleTimelineEvents,
    primaryColor: '#4A90E2',
    secondaryColor: '#7B68EE',
    backgroundColor: '#0F1419',
    showProgress: true,
  },
};
